import axios from "axios";
import { create } from "zustand";

export interface IDeviceCategory {
  deviceCategoryID: number;
  deviceCategoryName: string;
  deviceCategoryDesc: string;
}

export interface IDevice {
  deviceID: number;
  deviceCategoryID: number;
  deviceName: string;
  deviceDesc: string;
  deviceImageUrl: string;
  deviceDetailUrl: string;
  export: boolean;
  billTag: string | null;
  addBillTags: string[] | null;
  defaultUpdateRateID: number | null;
  updateRateIDs: number[];
}

export interface IGroupedDevices {
  name: string;
  deviceID: number;
  deviceCategoryID: number;
  description: string | null;
  detailUrl: string | null;
  imageUrl: string | null;
  image: string | null;
  children: IGroupedDevices[];
}

export interface IDeviceUpdateRateInfo {
  defaultUpdateRateID: number | null;
  updateRateIDs: number[];
}

type DeviceStore = {
  devices: IGroupedDevices[];
  deviceMap: Record<number, IDevice>;
  loading: Boolean;
  hasErrors: Boolean;
  fetchDevices: (apiBaseUrl: string) => Promise<void>;
  lookupCategory: (deviceID: number) => number;
  lookupUpdateRate: (deviceID: number) => IDeviceUpdateRateInfo;
  lookupDeviceImage: (deviceID: number) => string;
  lookupDeviceName: (deviceID: number) => string;
  lookupExport: (deviceID: number) => boolean;
  lookupBillTags: (deviceID: number) => string[];
};

const useDeviceStore = create<DeviceStore>((set, get) => ({
  devices: [],
  deviceMap: {},
  loading: false,
  hasErrors: false,
  fetchDevices: async (apiBaseUrl) => {
    // Ignore this call if base URL is empty
    if (apiBaseUrl === "") {
      console.log("fetchDevices: apiBaseUrl is empty, ignoring");
      return;
    }

    set(() => ({ loading: true }));
    try {
      console.log("Fetching devices");
      const response = await axios.get<{
        deviceCategories: IDeviceCategory[];
        devices: IDevice[];
      }>(`${apiBaseUrl}/api/device/devices`);
      console.log(response.data);
      const deviceCategories = response.data.deviceCategories;
      const devices = response.data.devices;
      // The outer container will be categories, children will be the devices
      const groupedData: IGroupedDevices[] = deviceCategories.map((category) => {
        return {
          name: category.deviceCategoryName,
          deviceID: -1,
          deviceCategoryID: category.deviceCategoryID,
          detailUrl: null,
          imageUrl: null,
          image: null,
          description: null,
          children: devices
            .filter((device) => device.deviceCategoryID === category.deviceCategoryID)
            .map((device) => {
              return {
                name: device.deviceName,
                deviceID: device.deviceID,
                deviceCategoryID: device.deviceCategoryID,
                detailUrl: device.deviceDetailUrl,
                imageUrl: device.deviceImageUrl,
                image: null,
                description: device.deviceDesc,
                children: [],
              };
            }),
        };
      });
      console.log(groupedData);
      // Create map for N(1) lookup
      const map: Record<number, IDevice> = {};
      devices.forEach((dev) => {
        map[dev.deviceID] = dev;
      });

      set(() => ({
        devices: groupedData,
        deviceMap: map,
        loading: false,
      }));
    } catch (err) {
      set(() => ({ hasErrors: true, loading: false }));
    }
  },

  lookupCategory: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev ? dev.deviceCategoryID : -1;
  },
  lookupUpdateRate: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev
      ? { defaultUpdateRateID: dev.defaultUpdateRateID, updateRateIDs: dev.updateRateIDs }
      : { defaultUpdateRateID: null, updateRateIDs: [] };
  },
  lookupDeviceImage: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev ? dev.deviceImageUrl : "";
  },
  lookupDeviceName: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev ? dev.deviceName : "";
  },
  lookupExport: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev ? dev.export : false;
  },
  lookupBillTags: (deviceID) => {
    var dev = get().deviceMap[deviceID];
    return dev.billTag ? dev.addBillTags ? [dev.billTag, ...dev.addBillTags] : [dev.billTag] : []
  },
}));

export default useDeviceStore;
